import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star } from 'lucide-react';
import { ApiService } from '../services/apiEndpoints';
import { useAuth } from '../context/AuthContext';
import './Feedback.css';

// Helper to format ISO strings to "d MMM, yyyy"
const formatFeedbackDate = (raw) => {
  try {
    const date = new Date(raw);
    if (isNaN(date.getTime())) return "Recent";
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch (e) {
    return "Recent";
  }
};

const Feedback = () => {
  const navigate = useNavigate();
  const { studentId } = useAuth(); // Replaces Session.studentId
  
  // Form & History State
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Fetch History (Replaces LaunchedEffect)
  const loadHistory = async () => {
    if (!studentId) return;
    try {
      const response = await ApiService.getFeedbackHistory(studentId);
      if (response.data) {
        // Sort descending by createdAt
        const sorted = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setHistory(sorted);
      }
    } catch (error) {
      console.error("Failed to load feedback history", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [studentId]);

  // Submit Handler (Replaces CoroutineScope launch)
  const handleSubmit = async () => {
    if (rating === 0 || submitting) return;
    setSubmitting(true);
    try {
      await ApiService.sendFeedback({
        studentId,
        message,
        rating
      });
      // Reset form and refresh list
      setMessage("");
      setRating(0);
      await loadHistory();
    } catch (error) {
      console.error("Failed to submit feedback", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-screen">
      {/* Top Bar */}
      <div className="top-bar">
        <button onClick={() => navigate(-1)} className="icon-button">
          <ArrowLeft size={24} color="#1e3a8a" />
        </button>
        <h2 className="top-bar-title">Feedback</h2>
        <div style={{ width: 24 }}></div> {/* Spacer to center title */}
      </div>

      <div className="scroll-content">
        {/* Header Section */}
        <div className="header-text">
          <h1>We value your opinion</h1>
          <p>Let us know how we can improve your experience.</p>
        </div>

        {/* Submit Card */}
        <div className="feedback-card submit-card">
          <h3>Rate your experience</h3>
          
          <div className="star-rating-bar">
            {[1, 2, 3, 4, 5].map((star) => (
              <button 
                key={star}
                type="button"
                className="star-btn"
                onClick={() => setRating(star)}
              >
                <Star 
                  size={36} 
                  color={star <= rating ? "var(--warning-amber, #d97706)" : "var(--text-light, #94a3b8)"}
                  fill={star <= rating ? "var(--warning-amber, #d97706)" : "transparent"} 
                />
              </button>
            ))}
          </div>

          <textarea 
            className="feedback-textarea"
            placeholder="Tell us more details..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          <button 
            className="submit-button"
            onClick={handleSubmit}
            disabled={rating === 0 || submitting}
          >
            {submitting ? <div className="spinner"></div> : "Submit Review"}
          </button>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <div className="history-section">
            <h2 className="history-title">Your History</h2>
            
            {loadingHistory ? (
              <p className="loading-text">Loading...</p>
            ) : (
              <div className="history-list">
                {history.map((item, index) => (
                  <div key={index} className="feedback-card history-card">
                    
                    <div className="history-card-header">
                      <div className="mini-stars">
                        {/* Render 5 mini stars based on rating */}
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star}
                            size={16} 
                            color={star <= item.rating ? "var(--warning-amber, #d97706)" : "var(--text-light, #94a3b8)"}
                            fill={star <= item.rating ? "var(--warning-amber, #d97706)" : "transparent"} 
                            opacity={star <= item.rating ? 1 : 0.5}
                          />
                        ))}
                      </div>
                      <span className="history-date">{formatFeedbackDate(item.createdAt)}</span>
                    </div>

                    <p className="history-message">{item.message}</p>

                    {/* Admin Reply Block */}
                    {item.reply && (
                      <div className="admin-reply-box">
                        <span className="admin-reply-label">Admin Response</span>
                        <p className="admin-reply-text">{item.reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Feedback;
